import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import {
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  Trash2,
  Sparkles,
} from "lucide-react";
import { useParserPipeline } from "./useParserPipeline";
import type { CountyAdapter } from "./types";

interface Props {
  adapter: CountyAdapter;
  onImportComplete?: () => void;
}

export function GenericCountyParser({ adapter, onImportComplete }: Props) {
  const {
    rawText,
    setRawText,
    isProcessing,
    progress,
    result,
    run,
    runAi,
  } = useParserPipeline(adapter, onImportComplete);
  const { toast } = useToast();

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setRawText(text);
    toast({
      title: "File loaded",
      description: `Loaded ${file.name} - ${text.split("\n").length} lines`,
    });
  };

  return (
    <div className="space-y-4">
      {result && (
        <Alert
          className={
            result.imported > 0 ? "border-green-500" : "border-yellow-500"
          }
        >
          <CheckCircle className="h-4 w-4" />
          <AlertTitle>Import Results</AlertTitle>
          <AlertDescription>
            <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
              <div>
                Total Parsed: <strong>{result.totalParsed}</strong>
              </div>
              {result.adapterRejected > 0 && (
                <div>
                  Adapter Rejected:{" "}
                  <strong>{result.adapterRejected}</strong>
                </div>
              )}
              {result.sourceDuplicatesSkipped > 0 && (
                <div>
                  Duplicates in Paste:{" "}
                  <strong>{result.sourceDuplicatesSkipped}</strong>
                </div>
              )}
              <div>
                Family Transfers: <strong>{result.familyFiltered}</strong>
              </div>
              <div>
                Business-to-Business:{" "}
                <strong>{result.businessFiltered}</strong>
              </div>
              <div>
                Bank Transactions: <strong>{result.bankFiltered}</strong>
              </div>
              <div>
                Church/IRA: <strong>{result.excludedFiltered}</strong>
              </div>
              <div>
                Already in Database:{" "}
                <strong>{result.existingSkipped}</strong>
              </div>
              <div className="text-green-600">
                Imported: <strong>{result.imported}</strong>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {adapter.countyLabel}
          </CardTitle>
          <CardDescription>{adapter.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <label className="cursor-pointer">
                <Upload className="h-4 w-4 mr-2" />
                Upload Text File
                <input
                  type="file"
                  accept=".txt"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            </Button>
            <Button
              variant="outline"
              onClick={() => setRawText("")}
              disabled={!rawText}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </Button>
          </div>

          <Textarea
            placeholder={adapter.placeholder}
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            className="min-h-[300px] font-mono text-xs"
          />

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground text-center">
                Processing... {Math.round(progress)}%
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={run}
              disabled={isProcessing || !rawText.trim()}
              className="flex-1"
            >
              {isProcessing ? "Processing..." : "Parse & Import Records"}
            </Button>
            <Button
              variant="secondary"
              onClick={runAi}
              disabled={isProcessing || !rawText.trim()}
              title="Use AI to parse glued/messy paste formats. Slower; uses AI credits."
            >
              <Sparkles className="h-4 w-4 mr-2" />
              AI Parse (fallback)
            </Button>
          </div>
        </CardContent>
      </Card>

      {adapter.filterRules && adapter.filterRules.length > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Filtering Rules</AlertTitle>
          <AlertDescription className="text-xs space-y-1">
            {adapter.filterRules.map((rule, i) => (
              <p key={i}>• {rule}</p>
            ))}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
